import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getSubscription, cancelSubscription, resumeSubscription, getInvoices, createSetupIntentSession,
} from '../api';
import EmbeddedCheckoutModal from '../components/EmbeddedCheckoutModal';

const TIER_LABELS = {
  free: { label: 'Free', color: 'var(--muted)' },
  basic: { label: 'Basic', color: 'var(--blue)' },
  pro: { label: 'Pro', color: 'var(--teal)' },
};

const CARD_BRAND_LABELS = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover',
};

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function fmtAmount(cents, currency) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency', currency: (currency || 'usd').toUpperCase(),
  }).format((cents || 0) / 100);
}

export default function BillingPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpdatePayment, setShowUpdatePayment] = useState(false);

  const loadSubscription = useCallback(() => {
    setLoading(true);
    return getSubscription()
      .then(({ data }) => setSub(data))
      .catch(() => setError('Failed to load billing information.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  useEffect(() => {
    getInvoices()
      .then(({ data }) => setInvoices(data))
      .catch(() => {})
      .finally(() => setInvoicesLoading(false));
  }, []);

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You will keep access until the end of your current billing period.')) return;
    setActionLoading(true);
    setError('');
    try {
      const { data } = await cancelSubscription();
      setSub(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel subscription. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    setError('');
    try {
      const { data } = await resumeSubscription();
      setSub(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resume subscription. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchSetupClientSecret = useCallback(async () => {
    const res = await createSetupIntentSession();
    return res.data.client_secret;
  }, []);

  const handleUpdatePaymentComplete = async () => {
    setShowUpdatePayment(false);
    await loadSubscription();
    await refreshUser();
  };

  const tierMeta = TIER_LABELS[sub?.tier] || TIER_LABELS.free;
  // A failed renewal drops the account to Free but leaves the Stripe subscription
  // past_due with an open invoice. Paying that invoice restores the plan; starting
  // a new plan from Pricing would create a second subscription.
  const paymentIssue = !sub?.has_subscription && ['past_due', 'unpaid'].includes(sub?.subscription_status);
  const openInvoice = invoices.find((inv) => inv.status === 'open' && inv.hosted_invoice_url);

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: 24 }}>
      <Helmet>
        <title>Billing — Algovex</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 20px', color: 'var(--text)' }}>Billing</h1>

      {error && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>Loading…</div>
      ) : (
        <>
          {/* ── Plan ── */}
          <div style={sectionStyle}>
            <div style={cardLabelStyle}>Plan</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{
                padding: '4px 12px', fontSize: 13, fontWeight: 700,
                background: tierMeta.color, color: 'var(--bg)',
                borderRadius: 6, textTransform: 'uppercase',
              }}>
                {tierMeta.label}
              </span>
              {sub?.subscription_status && (
                <span style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'capitalize' }}>
                  {sub.subscription_status.replace('_', ' ')}
                </span>
              )}
            </div>

            {sub?.has_subscription && sub?.current_period_end && (
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
                {sub.cancel_at_period_end ? (
                  <>Your plan will be canceled on <strong style={{ color: 'var(--text)' }}>{fmtDate(sub.current_period_end)}</strong>. You'll keep access until then.</>
                ) : (
                  <>Renews on <strong style={{ color: 'var(--text)' }}>{fmtDate(sub.current_period_end)}</strong>.</>
                )}
              </div>
            )}

            {paymentIssue && (
              <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 14, lineHeight: 1.6, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6 }}>
                Your last payment didn't go through, so your account is on the Free plan for now.
                {' '}Pay the open invoice to restore your plan. You can use a different card.
              </div>
            )}

            {paymentIssue ? (
              openInvoice ? (
                <a href={openInvoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer" style={{ ...btnStyle('primary', false), display: 'inline-block', textDecoration: 'none' }}>
                  Pay open invoice ({fmtAmount(openInvoice.amount_due, openInvoice.currency)}) →
                </a>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {invoicesLoading ? 'Loading invoices…' : 'No open invoice found. Use the Feedback button and we\'ll sort it out.'}
                </div>
              )
            ) : !sub?.has_subscription ? (
              <button onClick={() => navigate('/pricing')} style={linkBtnStyle}>
                View plans &amp; upgrade →
              </button>
            ) : sub.cancel_at_period_end ? (
              <button onClick={handleResume} disabled={actionLoading} style={btnStyle('primary', actionLoading)}>
                {actionLoading ? 'Working…' : 'Resume subscription'}
              </button>
            ) : (
              <button onClick={handleCancel} disabled={actionLoading} style={btnStyle('danger', actionLoading)}>
                {actionLoading ? 'Working…' : 'Cancel subscription'}
              </button>
            )}
          </div>

          {/* ── Payment method ── */}
          {sub?.has_subscription && (
            <div style={sectionStyle}>
              <div style={cardLabelStyle}>Payment method</div>
              {sub.payment_method ? (
                <div style={{ fontSize: 14, color: 'var(--text)', marginBottom: 14 }}>
                  {CARD_BRAND_LABELS[sub.payment_method.brand] || sub.payment_method.brand} •••• {sub.payment_method.last4}
                  <span style={{ color: 'var(--muted)', marginLeft: 8, fontSize: 12 }}>
                    Exp {String(sub.payment_method.exp_month).padStart(2, '0')}/{sub.payment_method.exp_year}
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>No payment method on file.</div>
              )}
              <button onClick={() => setShowUpdatePayment(true)} style={linkBtnStyle}>
                Update payment method →
              </button>
            </div>
          )}

          {/* ── Payment history ── */}
          <div style={sectionStyle}>
            <div style={cardLabelStyle}>Payment history</div>
            {invoicesLoading ? (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</div>
            ) : invoices.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>No payments yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {invoices.map((inv) => (
                  <a
                    key={inv.id}
                    href={inv.hosted_invoice_url || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                      padding: '10px 12px', borderRadius: 6, border: '1px solid var(--border)',
                      textDecoration: 'none', color: 'inherit', fontSize: 13,
                    }}
                  >
                    <span>{fmtDate(inv.date)}</span>
                    <span style={{ fontWeight: 700 }}>{fmtAmount(inv.status === 'paid' ? inv.amount_paid : inv.amount_due, inv.currency)}</span>
                    <span style={{
                      textTransform: 'capitalize', fontSize: 11, fontWeight: 700,
                      color: inv.status === 'paid' ? 'var(--teal)' : inv.status === 'open' ? '#f87171' : 'var(--muted)',
                    }}>
                      {inv.status === 'open' ? 'unpaid — pay now' : inv.status}
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {showUpdatePayment && (
        <EmbeddedCheckoutModal
          title="Update payment method"
          fetchClientSecret={fetchSetupClientSecret}
          onComplete={handleUpdatePaymentComplete}
          onClose={() => setShowUpdatePayment(false)}
        />
      )}
    </div>
  );
}

const sectionStyle = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 20,
  marginBottom: 16,
};

const cardLabelStyle = {
  fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 12,
  textTransform: 'uppercase', letterSpacing: 1,
};

const linkBtnStyle = {
  background: 'none', border: '1px solid var(--teal)',
  color: 'var(--teal)', padding: '6px 14px', borderRadius: 6,
  fontSize: 12, fontWeight: 700, cursor: 'pointer',
};

function btnStyle(variant, disabled) {
  const colors = variant === 'danger'
    ? { border: '1px solid #ef4444', color: '#ef4444', background: 'none' }
    : { border: 'none', color: 'var(--bg)', background: 'var(--teal)' };
  return {
    ...colors,
    padding: '8px 16px', borderRadius: 6,
    fontSize: 12, fontWeight: 700,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };
}
